<template>
  <div class="max-h-56 overflow-y-scroll">
    <table class="w-full">
      <tbody>
        <tr>
          <th v-if="selectable"></th>
          <th>Description</th>
          <th>Accession Number</th>
          <th>Date</th>
          <th>Modalities</th>
          <th>Images</th>
        </tr>
        <template v-if="tableData.length === 0">
          <tr>
            <td>{{ "No results" }}</td>
          </tr>
        </template>
        <template v-for="row in tableData" :key="(row['0020000D']?.Value?.[0] as string | undefined)">
          <tr
            @click="handleRowClick(row)"
            class="bg-base-200"
            :class="{ 'border-b border-base-content/40': !row.series }"
            :id="
              row['00080050']?.Value?.[0]
                ? 'study' + row['00080050']?.Value?.[0]
                : undefined
            "
          >
            <td v-if="selectable" style="text-align: center">
              <input
                type="checkbox"
                class="checkbox checkbox-sm"
                v-model="row.selected"
                @change="handleRowSelectChange(row)"
              />
            </td>
            <td>{{ row["00081030"]?.Value?.[0] || "Unnamed study" }}</td>
            <td>{{ row["00080050"]?.Value?.[0] || "No accession" }}</td>
            <td>
              {{
                row["00080020"]?.Value?.[0]
                  ? formatDate(row["00080020"].Value[0])
                  : "Unknown date"
              }}
            </td>
            <td>{{ row["00080061"]?.Value?.[0] }}</td>
            <td>{{ row["00201208"]?.Value?.[0] }}</td>
          </tr>
          <tr
            v-if="row.series?.length"
            :key="row['0020000D']?.Value?.[0] + '1'"
            class="series-row bg-base-100"
            :class="{ 'border-b border-base-content/40': row.series }"
          >
            <td colspan="100">
              <series-list-select
                :series-list="row.series"
                @update="emit('selectionUpdated')"
                :selectable="selectable"
              />
            </td>
          </tr>
        </template>
      </tbody>
    </table>
  </div>
</template>

<script setup lang="ts">
import parse from "date-fns/parse";
import format from "date-fns/format";
import SeriesListSelect from "./SeriesListSelect.vue";
import { ResultSeries, ResultStudy } from "./types";
import { getClients } from "./dicomweb-client";
import { computed, onMounted, watch } from "vue";
import { addLog } from "../../logger";
import seriesDisallowed from "./seriesAllowed";

const props = defineProps<{
  tableData: ResultStudy[];
  selectable: boolean;
  currentAccession: string;
}>();
const emit = defineEmits(["selectionUpdated"]);

const { qidoClient } = getClients();

onMounted(() => {
  loadSelectedStudySeries();
  scrollMatchedAccessionIntoView();
});

const selectedStudiesUnloaded = computed(() => {
  return props.tableData.filter((row) => row.selected && !row.series);
});

async function loadSelectedStudySeries() {
  selectedStudiesUnloaded.value.forEach((row) => handleRowClick(row));
}

watch(selectedStudiesUnloaded, () => {
  loadSelectedStudySeries();
});

watch(
  () => props.tableData,
  () => {
    scrollMatchedAccessionIntoView();
  }
);

function scrollMatchedAccessionIntoView() {
  console.log("here");
  const matchedAccessionEl = document.getElementById(
    "study" + props.currentAccession
  );
  if (matchedAccessionEl) {
    matchedAccessionEl.scrollIntoView();
  }
}

async function handleRowClick(row: ResultStudy) {
  row.selected = true;

  if (!row.series) {
    try {
      let seriesResults: ResultSeries[] = await qidoClient.searchForSeries({
        studyInstanceUID: row["0020000D"]?.Value?.[0],
      });

      seriesResults = seriesResults.filter((result) => {
        return typeof result["0020000E"]?.Value?.[0] === "string";
      });

      // Check if allowed
      seriesResults.forEach((series) => {
        series.disallowed = seriesDisallowed(series);
      });

      // Combine series if they have the same SeriesInstanceUID (Ultrasound sometimes)
      seriesResults = seriesResults.reduce((acc, current) => {
        const seriesUID = current["0020000E"]?.Value?.[0];
        const existing = acc.find(
          (ser) => ser["0020000E"]?.Value?.[0] === seriesUID
        );
        if (existing?.["00201209"]) {
          const seriesRelatedInstances = Number(current["00201209"]?.Value[0]);
          existing["00201209"].Value[0] = (
            Number(existing["00201209"].Value[0]) + seriesRelatedInstances
          ).toString();
          existing.combined = existing.combined ? existing.combined + 1 : 1;
        } else {
          acc.push(current);
        }
        return acc;
      }, [] as ResultSeries[]);
      row.series = seriesResults;
    } catch (err) {
      addLog("error", (err as any).message || err);
      console.error(err);
    }
  }
  handleRowSelectChange(row); // select all series by default
}

function handleRowSelectChange(row: ResultStudy) {
  if (!row.series) return;
  if (row.selected) {
    // select all series
    row.series.forEach((s) => {
      if (!s.disallowed) s.selected = true;
    });
  } else {
    // unselect all series
    row.series.forEach((s) => (s.selected = false));
  }
  emit("selectionUpdated");
}

function formatDate(dateString: string) {
  if (!dateString) return "N/A";
  const date = parse(dateString, "yyyyMMdd", new Date());
  return format(date, "M/d/yyyy");
}
</script>

<style scoped>
th {
  text-align: left;
}
.series-row td {
  padding-left: 50px;
}
tr:first-of-type {
  background: #4f81b5;
  color: white;
}
th,
td {
  padding: 5px;
}
</style>
